import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import React from "react";

// Hardcoded super admin email - only this user can see console and DevTools
const SUPER_ADMIN_EMAIL = "palavancaner@gmail.com";

// Store original console for super admin
const originalConsole = { ...window.console };

// Disable console for non-super-admin users
const disableConsoleForNonAdmin = (userEmail) => {
  if (userEmail !== SUPER_ADMIN_EMAIL) {
    const noop = () => {};
    window.console = {
      log: noop,
      warn: noop,
      error: noop,
      info: noop,
      debug: noop,
      trace: noop,
      dir: noop,
      dirxml: noop,
      table: noop,
      group: noop,
      groupCollapsed: noop,
      groupEnd: noop,
      clear: noop,
      count: noop,
      countReset: noop,
      assert: noop,
      profile: noop,
      profileEnd: noop,
      time: noop,
      timeLog: noop,
      timeEnd: noop,
      timeStamp: noop,
    };
  } else {
    // Restore console for super admin
    window.console = originalConsole;
  }
};

// DevTools detection and blocking for non-super-admin users
const setupDevToolsBlocker = (userEmail) => {
  if (userEmail === SUPER_ADMIN_EMAIL) return;

  // Method 1: Detect via debugger timing
  let devtoolsOpen = false;
  
  const detectDevTools = () => {
    const start = performance.now();
    debugger;
    const end = performance.now();
    
    if (end - start > 100) {
      devtoolsOpen = true;
      handleDevToolsOpen();
    }
  };

  // Method 2: Detect via window size
  const checkWindowSize = () => {
    const widthThreshold = window.outerWidth - window.innerWidth > 160;
    const heightThreshold = window.outerHeight - window.innerHeight > 160;
    
    if (widthThreshold || heightThreshold) {
      handleDevToolsOpen();
    }
  };

  // Method 3: Detect via console property
  const detectViaConsole = () => {
    const element = new Image();
    Object.defineProperty(element, 'id', {
      get: function() {
        handleDevToolsOpen();
      }
    });
  };

  const handleDevToolsOpen = () => {
    // Show warning but don't redirect - just log
    console.warn('DevTools detected');
  };

  // Disable right-click
  document.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    return false;
  });

  // Disable keyboard shortcuts for DevTools
  document.addEventListener('keydown', (e) => {
    // F12
    if (e.key === 'F12') {
      e.preventDefault();
      return false;
    }
    // Ctrl+Shift+I (DevTools)
    if (e.ctrlKey && e.shiftKey && e.key === 'I') {
      e.preventDefault();
      return false;
    }
    // Ctrl+Shift+J (Console)
    if (e.ctrlKey && e.shiftKey && e.key === 'J') {
      e.preventDefault();
      return false;
    }
    // Ctrl+Shift+C (Inspect Element)
    if (e.ctrlKey && e.shiftKey && e.key === 'C') {
      e.preventDefault();
      return false;
    }
    // Ctrl+U (View Source)
    if (e.ctrlKey && e.key === 'u') {
      e.preventDefault();
      return false;
    }
    // Cmd+Option+I (Mac DevTools)
    if (e.metaKey && e.altKey && e.key === 'i') {
      e.preventDefault();
      return false;
    }
    // Cmd+Option+J (Mac Console)
    if (e.metaKey && e.altKey && e.key === 'j') {
      e.preventDefault();
      return false;
    }
    // Cmd+Option+C (Mac Inspect)
    if (e.metaKey && e.altKey && e.key === 'c') {
      e.preventDefault();
      return false;
    }
  });

  // Check periodically
  setInterval(checkWindowSize, 1000);
  
  // Initial check
  detectViaConsole();
};

// Pages
import LandingPage from "./pages/LandingPage";
import SetupPage from "./pages/SetupPage";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Audits from "./pages/Audits";
import AuditDetail from "./pages/AuditDetail";
import AuditCalendar from "./pages/AuditCalendar";
import Reports from "./pages/Reports";
import ReportDetail from "./pages/ReportDetail";
import ReportReview from "./pages/ReportReview";
import InteractiveReportForm from "./pages/InteractiveReportForm";
import Templates from "./pages/Templates";
import Users from "./pages/Users";
import SettingsPage from "./pages/SettingsPage";
import DriveCallback from "./pages/DriveCallback";
import ExpensesPage from "./pages/ExpensesPage";
import NotificationsPage from "./pages/NotificationsPage";
import MessagesPage from "./pages/MessagesPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CalendarCallback from "./pages/CalendarCallback";
import RoyalCertAuditForm from "./pages/RoyalCertAuditForm";
import RoyalCertStage2Form from "./pages/RoyalCertStage2Form";
import ManDayCalculatorPage from "./pages/ManDayCalculatorPage";
import ChecklistTemplates from "./pages/ChecklistTemplates";
import ChecklistTemplateEditor from "./pages/ChecklistTemplateEditor";
import CertificatesPage from "./pages/CertificatesPage";
import FR48DecisionPage from "./pages/FR48DecisionPage";
// SalesPage removed - merged with Customers
import Layout from "./components/Layout";
import OfflineIndicator from "./components/OfflineIndicator";
import AuthCallbackPage from "./pages/AuthCallback";
import { setupMidnightLogout, clearAllCaches, checkSessionExpiry } from "./utils/sessionSecurity";
import { TenantProvider } from "./contexts/TenantContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get tenant slug from hostname (for axios interceptor)
const getTenantSlugFromHostname = () => {
  const hostname = window.location.hostname;
  // Skip development environments
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('emergentagent.com')) {
    return null;
  }
  // Production: extract subdomain from trb.surdurulebilirdenetim.com or royalcert.surdurulebilirdenetim.com
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    const subdomain = parts[0].toLowerCase();
    if (!['www', 'api', 'portal', 'app'].includes(subdomain)) {
      return subdomain;
    }
  }
  return null;
};

// Map slug to tenant ID
const SLUG_TO_TENANT_ID = {
  'trb': 'tenant_trb_default',
  'royalcert': 'tenant_royalcert_default'
};

// Setup axios interceptor to add tenant headers to all requests
axios.interceptors.request.use((config) => {
  // First try localStorage (set by TenantContext)
  let tenantId = localStorage.getItem('currentTenantId');
  let tenantSlug = localStorage.getItem('currentTenantSlug');
  
  // Fallback: detect from hostname if localStorage is empty
  if (!tenantSlug) {
    const hostnameSlug = getTenantSlugFromHostname();
    if (hostnameSlug && SLUG_TO_TENANT_ID[hostnameSlug]) {
      tenantSlug = hostnameSlug;
      tenantId = SLUG_TO_TENANT_ID[hostnameSlug];
      // Store for future requests
      localStorage.setItem('currentTenantSlug', tenantSlug);
      localStorage.setItem('currentTenantId', tenantId);
    }
  }
  
  if (tenantId) {
    config.headers['X-Tenant-ID'] = tenantId;
  }
  if (tenantSlug) {
    config.headers['X-Tenant-Slug'] = tenantSlug;
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Auth Context
export const AuthContext = React.createContext(null);

// Auth Callback Component (Legacy - Emergent OAuth)
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
          // Clear hash and navigate to dashboard with user data
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/dashboard", { state: { user: response.data }, replace: true });
        } catch (error) {
          // Auth error - silent fail
          // Show error message to user
          const errorMessage = error.response?.data?.detail || "Giriş yapılamadı";
          toast.error(errorMessage);
          // Clear hash and go back to landing
          window.history.replaceState(null, "", "/");
          navigate("/", { replace: true });
        }
      } else {
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Giriş yapılıyor...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const midnightLogoutSetup = useRef(false);
  const sessionChecked = useRef(false);

  // Check if session expired (new day since last activity)
  useEffect(() => {
    if (sessionChecked.current) return;
    sessionChecked.current = true;

    const checkSession = async () => {
      // Skip session expiry check when offline
      if (!navigator.onLine) {
        // Offline mode: Skipping session expiry check
        return;
      }
      
      const expired = await checkSessionExpiry();
      if (expired) {
        // Session expired - redirecting to login
        toast.info('Güvenlik nedeniyle oturumunuz sonlandırıldı. Lütfen tekrar giriş yapın.');
        // Call logout API
        try {
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (e) {}
        localStorage.removeItem('cachedUser');
        navigate("/", { replace: true });
      }
    };

    checkSession();
  }, [navigate]);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      // If offline, try to use cached user data
      if (!navigator.onLine) {
        const cachedUser = localStorage.getItem('cachedUser');
        if (cachedUser) {
          try {
            const userData = JSON.parse(cachedUser);
            setUser(userData);
            setIsAuthenticated(true);
            // Disable console for non-super-admin
            disableConsoleForNonAdmin(userData?.email);
            return;
          } catch (e) {
            // Silent fail
          }
        }
      }

      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
        // Disable console for non-super-admin
        disableConsoleForNonAdmin(response.data?.email);
        // Cache user data for offline use
        localStorage.setItem('cachedUser', JSON.stringify(response.data));
      } catch (error) {
        // If offline and we have no cached data, don't logout - just show offline message
        if (!navigator.onLine) {
          // Keep user logged in if they were already authenticated
          const cachedUser = localStorage.getItem('cachedUser');
          if (cachedUser) {
            try {
              const userData = JSON.parse(cachedUser);
              setUser(userData);
              setIsAuthenticated(true);
              // Disable console for non-super-admin
              disableConsoleForNonAdmin(userData?.email);
              return;
            } catch (e) {}
          }
        }
        setIsAuthenticated(false);
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  // Setup midnight logout when authenticated
  useEffect(() => {
    if (isAuthenticated && !midnightLogoutSetup.current) {
      midnightLogoutSetup.current = true;
      
      const cleanup = setupMidnightLogout(
        // onLogout
        async () => {
          try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
          } catch (e) {
            // Logout API call failed, but cache cleared
          }
        },
        // onWarning
        (message) => {
          toast.warning(message, { duration: 60000 });
        }
      );
      
      return cleanup;
    }
  }, [isAuthenticated]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Check if user has any of the allowed roles (support multiple roles)
  if (allowedRoles.length > 0) {
    const userRoles = user?.roles || [user?.role];
    const hasAccess = userRoles.some(role => allowedRoles.includes(role)) || userRoles.includes('super_admin');
    if (!hasAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return (
    <AuthContext.Provider value={{ user, setUser, clearAllCaches }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id SYNCHRONOUSLY during render (Emergent OAuth)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/api/calendar/callback" element={<CalendarCallback />} />
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/api/drive/callback" element={<DriveCallback />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/customers" element={
        <ProtectedRoute allowedRoles={["admin", "planning_expert", "sales_representative"]}>
          <Layout><Customers /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/customers/:id" element={
        <ProtectedRoute>
          <Layout><CustomerDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/audits" element={
        <ProtectedRoute>
          <Layout><Audits /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/audits/:id" element={
        <ProtectedRoute>
          <Layout><AuditDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <Layout><AuditCalendar /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <Layout><Reports /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports/:id" element={
        <ProtectedRoute>
          <Layout><ReportDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/interactive-report/new" element={
        <ProtectedRoute>
          <Layout><InteractiveReportForm /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/interactive-report/:id" element={
        <ProtectedRoute>
          <Layout><InteractiveReportForm /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/audit/royalcert/:auditId" element={
        <ProtectedRoute>
          <Layout><RoyalCertAuditForm /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/royalcert-audit/:auditRecordId" element={
        <ProtectedRoute>
          <Layout><RoyalCertAuditForm /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/audit/royalcert-stage2/:auditId" element={
        <ProtectedRoute>
          <Layout><RoyalCertStage2Form /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/manday-calculator" element={
        <ProtectedRoute allowedRoles={["admin", "super_admin", "planning_expert", "sales_representative"]}>
          <Layout><ManDayCalculatorPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports/review" element={
        <ProtectedRoute allowedRoles={["admin", "technical_manager"]}>
          <Layout><ReportReview /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/templates" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><Templates /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><Users /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/ayarlar" element={
        <ProtectedRoute allowedRoles={["admin", "technical_manager", "planning_expert", "auditor", "sales_representative"]}>
          <Layout><SettingsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/masraflar" element={
        <ProtectedRoute allowedRoles={["admin", "technical_manager", "planning_expert", "auditor"]}>
          <Layout><ExpensesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/bildirimler" element={
        <ProtectedRoute allowedRoles={["admin", "technical_manager", "planning_expert", "auditor", "sales_representative"]}>
          <Layout><NotificationsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/mesajlar" element={
        <ProtectedRoute allowedRoles={["admin", "technical_manager", "planning_expert", "auditor", "sales_representative"]}>
          <Layout><MessagesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/checklist-templates" element={
        <ProtectedRoute allowedRoles={[]}>
          <Layout><ChecklistTemplates /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/checklist-templates/:templateId" element={
        <ProtectedRoute allowedRoles={[]}>
          <Layout><ChecklistTemplateEditor /></Layout>
        </ProtectedRoute>
      } />
      {/* Certificate Management - RoyalCert */}
      <Route path="/certificates" element={
        <ProtectedRoute allowedRoles={["admin", "super_admin", "certificate_responsible"]}>
          <Layout><CertificatesPage /></Layout>
        </ProtectedRoute>
      } />
      {/* FR.48 Decision Management - RoyalCert */}
      <Route path="/fr48-decisions" element={
        <ProtectedRoute allowedRoles={["admin", "super_admin", "certificate_responsible"]}>
          <Layout><FR48DecisionPage /></Layout>
        </ProtectedRoute>
      } />
      {/* Redirect /satis to /customers */}
      <Route path="/satis" element={<Navigate to="/customers" replace />} />
    </Routes>
  );
}

function App() {
  // Disable console and DevTools immediately on app load for security
  // Only super admin (palavancaner@gmail.com) can see console after login
  useEffect(() => {
    // Check if user is already logged in from cache
    const cachedUser = localStorage.getItem('cachedUser');
    let userEmail = null;
    
    if (cachedUser) {
      try {
        const userData = JSON.parse(cachedUser);
        userEmail = userData?.email;
      } catch (e) {
        // Silent fail
      }
    }
    
    // Disable console and setup DevTools blocker
    disableConsoleForNonAdmin(userEmail);
    setupDevToolsBlocker(userEmail);
  }, []);

  return (
    <div className="App">
      <TenantProvider>
        <Toaster position="top-right" richColors />
        <OfflineIndicator />
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </TenantProvider>
    </div>
  );
}

export default App;
